import { queryHtmlElement } from '../../base/query-html-element.ts';
import { breakpoints } from '../../breakpoints.ts';

export class HeaderMainMenu {
  private elOpenBtn: HTMLElement;
  private elCloseBtn: HTMLElement;
  private elMenu: HTMLElement;
  private mqlXl: MediaQueryList;

  public constructor(elBtns: { open: HTMLElement; close: HTMLElement; }, elMenu: HTMLElement) {
    this.elOpenBtn = elBtns.open;
    this.elCloseBtn = elBtns.close;
    this.elMenu = elMenu;

    // define 'XL' breakpoint
    this.mqlXl = matchMedia(`(min-width: ${breakpoints.get('xl')}rem)`);

    this.bindEvents();
  }

  /**
   *
   */
  private bindEvents(): void {
    this.elOpenBtn?.addEventListener('click', this.open.bind(this));
    this.elCloseBtn?.addEventListener('click', this.close.bind(this));

    const elsSubControl = [...this.elMenu.querySelectorAll('.sub-control')]
      .filter(el => el instanceof HTMLElement);

    const setTabMediaAttributes = () => {
      // if media query matches (is XL+ size) collapse sub-menus
      if (this.mqlXl.matches) {
        for (const elTab of elsSubControl) {
          document.getElementById(elTab.getAttribute('aria-controls') ?? '')?.setAttribute('aria-expanded', 'false');
        }
      }
    };
    setTabMediaAttributes();

    // change role of top-level links depending on menu availability
    this.mqlXl.addEventListener('change', setTabMediaAttributes);

    // bind click listeners to open/close sub-menus
    for (const elTab of elsSubControl) {
      elTab.addEventListener('click', this.toggleSub.bind(this));
    }
  }

  /**
   * Open/Expand the menu
   */
  public open(ev: Event): void {
    ev?.preventDefault();
    this.elOpenBtn?.setAttribute('aria-pressed', 'true');
    this.elCloseBtn?.setAttribute('aria-pressed', 'false');
    this.elMenu.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close/collapse the menu
   */
  public close(ev: Event): void {
    ev?.preventDefault();
    this.elOpenBtn?.setAttribute('aria-pressed', 'false');
    this.elCloseBtn?.setAttribute('aria-pressed', 'true');
    this.elMenu.setAttribute('aria-expanded', 'false');
  }

  /**
   * Toggle the expanded state of a sub-menu item
   */
  public toggleSub(ev: Event): void {
    // if this is desktop layout, continue doing default action
    if (this.mqlXl.matches) {
      return;
    }

    const el = ev.currentTarget;
    if (el && el instanceof HTMLElement) {
      ev.preventDefault();

      // toggle the controlled list's aria-expanded state
      const elList = document.getElementById(el.getAttribute('aria-controls') ?? '');
      elList?.setAttribute('aria-expanded', elList.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
    }
  }
}

(() => {
  const onReady = () => {
    // create main menu functionality
    const elOpen = queryHtmlElement(document, '.header-main__menu-toggle');
    const elClose = queryHtmlElement(document, '.header-main__menu__close');
    const elMenu = queryHtmlElement(document, '.header-main__menu');
    if (elOpen !== null && elClose !== null && elMenu !== null) {
      new HeaderMainMenu({
        open: elOpen,
        close: elClose,
      }, elMenu);
    }

    // Toggle the social-media-icons variant between 'dark' (desktop nav bar) and
    // 'light' (mobile slide-out menu) to match the background the icons sit on.
    const elSocialIcons = queryHtmlElement(document, '.header-main--use-nav social-media-icons');
    if (elSocialIcons !== null) {
      const mql = matchMedia(`(min-width: ${breakpoints.get('xl')}rem)`);
      const applyVariant = () => elSocialIcons.setAttribute('variant', mql.matches ? 'dark' : 'light');
      mql.addEventListener('change', applyVariant);
      applyVariant();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
